// ============================================================================
// CENTRAL PROGRAM DATA — single source of truth for all programs & internships.
// Edit fees / topics / content here only. Detail pages + listings + homepage
// all read from this file (no duplicated hardcoded program content).
// ============================================================================

import { WHATSAPP } from "./site";

export const PROGRAMS_DATA = [
  // ------------------------------------------------------------------ PROGRAM 1
  {
    slug: "3-month-internship",
    category: "program", // -> /programs/:slug
    name: "3-Month Internship Program",
    homeSummary:
      "A focused, hands-on internship covering core AI, Python and a live project.",
    tagline:
      "Kick-start your AI journey with a practical, project-driven 3-month internship.",
    duration: "3 Months",
    mode: "Online / Offline / Hybrid",
    fee: "₹15,000",
    feeNote: "One-time program fee",
    isFree: false,
    highlight: false,
    overview:
      "The 3-Month Internship Program is designed for learners who want a strong, practical introduction to Artificial Intelligence. You will build foundational skills in AI, Generative AI, Machine Learning, Python and SQL, and apply them on a real live project — finishing with an internship certificate and resume guidance.",
    whoShouldJoin: [
      "Students and final-year graduates exploring an AI career",
      "Beginners who want practical, project-based exposure",
      "Professionals looking for a short, focused AI upskilling track",
    ],
    eligibility: [
      "Basic computer knowledge",
      "Interest in AI and problem-solving",
      "No prior coding experience mandatory",
    ],
    outcomes: [
      "Understand core AI, ML and Generative AI concepts",
      "Write practical Python and SQL for data tasks",
      "Complete and present a real live project",
      "Build an internship-ready resume",
    ],
    curriculum: [
      "Artificial Intelligence",
      "Generative AI",
      "Machine Learning Fundamentals",
      "Python",
      "SQL",
      "Live Project",
      "Resume Guidance",
      "Internship Certificate",
    ],
    certification:
      "On successful completion, you receive an ASAI Internship Certificate recognising your project work and skills.",
    careerSupport:
      "Includes resume guidance and career support. We provide placement assistance and interview readiness — we do not promise guaranteed placement.",
    faqs: [
      { q: "Do I need coding experience?", a: "No. We start from the fundamentals, so beginners can join comfortably." },
      { q: "Is the certificate provided?", a: "Yes, an ASAI Internship Certificate is awarded on successful completion." },
      { q: "What is the mode of learning?", a: "You can choose Online, Offline or Hybrid based on availability." },
    ],
    related: ["6-month-industrial-training", "1-year-job-oriented"],
  },

  // ------------------------------------------------------------------ PROGRAM 2
  {
    slug: "6-month-industrial-training",
    category: "program",
    name: "6-Month Internship and Industrial Training",
    homeSummary:
      "Extended internship with 90 hours of industrial training, capstone project and career support.",
    tagline:
      "Go deeper with 90 hours of industrial training, capstone project and interview preparation.",
    duration: "6 Months",
    mode: "Online / Offline / Hybrid",
    fee: "₹30,000",
    feeNote: "One-time program fee",
    isFree: false,
    highlight: false,
    overview:
      "The 6-Month Internship and Industrial Training program blends structured learning with 90 hours of industrial training. You will cover the applied AI and cloud stack, work on live projects and a capstone, and prepare for interviews — supported by an internship certificate and career support.",
    whoShouldJoin: [
      "Students seeking industry-grade, in-depth training",
      "Graduates preparing for AI, Data or Cloud roles",
      "Working professionals transitioning into AI/ML",
    ],
    eligibility: [
      "Basic computer knowledge",
      "Commitment to a 6-month structured schedule",
      "Interest in AI, data and cloud technologies",
    ],
    outcomes: [
      "Build end-to-end AI, ML and Data Science skills",
      "Work with Cloud Computing, DevOps, Linux and SQL",
      "Deliver live projects and a capstone project",
      "Be interview-ready with structured preparation",
    ],
    curriculum: [
      "90 Hours of Industrial Training",
      "Artificial Intelligence",
      "Machine Learning",
      "Generative AI",
      "Python",
      "Data Science",
      "Cloud Computing",
      "DevOps",
      "SQL",
      "Linux",
      "Live Projects",
      "Capstone Project",
      "Interview Preparation",
      "Internship Certificate",
      "Career Support",
    ],
    certification:
      "You receive an ASAI Internship & Industrial Training Certificate, including recognition of your capstone project.",
    careerSupport:
      "Includes interview preparation and ongoing career support with placement assistance. Placement is not guaranteed.",
    faqs: [
      { q: "What is industrial training?", a: "90 hours of guided, industry-style training that mirrors real workplace practices and tools." },
      { q: "Will I build real projects?", a: "Yes — multiple live projects plus a capstone project you can showcase to employers." },
      { q: "Is career support included?", a: "Yes, including interview preparation and placement assistance (not guaranteed placement)." },
    ],
    related: ["3-month-internship", "1-year-job-oriented"],
  },

  // ------------------------------------------------------------------ PROGRAM 3
  {
    slug: "1-year-job-oriented",
    category: "program",
    name: "1-Year Job-Oriented Program",
    homeSummary:
      "Comprehensive, career-focused program covering the full applied AI, cloud and job-readiness stack.",
    tagline:
      "A complete, job-oriented pathway — from AI foundations to placement preparation.",
    duration: "1 Year",
    mode: "Online / Offline / Hybrid",
    fee: "₹45,000",
    feeNote: "Full program fee",
    isFree: false,
    highlight: true,
    overview:
      "The 1-Year Job-Oriented Program is our most comprehensive track, built to make you industry- and job-ready. It spans Artificial Intelligence, Generative AI, Machine Learning, Cloud (AWS & Azure), DevOps, databases, Linux and full-stack fundamentals — combined with live projects, soft skills, resume/LinkedIn development, mock interviews and placement preparation.",
    whoShouldJoin: [
      "Students and graduates targeting a career in AI/Tech",
      "Career changers seeking a full job-ready program",
      "Anyone wanting depth across AI, cloud and job readiness",
    ],
    eligibility: [
      "Basic computer knowledge",
      "Commitment to a one-year learning journey",
      "Strong motivation to build a tech career",
    ],
    outcomes: [
      "Master applied AI, ML and Generative AI",
      "Gain hands-on Cloud (AWS/Azure), DevOps and Linux skills",
      "Build full-stack fundamentals and live projects",
      "Complete mock interviews and placement preparation",
    ],
    curriculum: [
      "Artificial Intelligence",
      "Generative AI",
      "Python",
      "Machine Learning",
      "SQL and Databases",
      "Linux",
      "Cloud Computing",
      "AWS and Azure",
      "DevOps",
      "Full-Stack Fundamentals",
      "Live Projects",
      "Soft Skills",
      "Resume and LinkedIn Development",
      "Mock Interviews",
      "Placement Preparation",
    ],
    certification:
      "You receive an ASAI Job-Oriented Program Certificate covering your complete skill set and projects.",
    careerSupport:
      "Comprehensive career support: resume & LinkedIn development, mock interviews and placement preparation with placement assistance. We do not promise guaranteed placement.",
    faqs: [
      { q: "Is this program for beginners?", a: "Yes — it starts from fundamentals and progresses to advanced, job-ready skills over one year." },
      { q: "Do you guarantee a job?", a: "No. We provide strong placement assistance, mock interviews and placement preparation, but placement is not guaranteed." },
      { q: "Which cloud platforms are covered?", a: "Both AWS and Azure, along with DevOps and Linux fundamentals." },
    ],
    related: ["6-month-industrial-training", "3-month-internship"],
  },

  // -------------------------------------------------------------- FREE INTERNSHIP 1
  {
    slug: "free-upi",
    category: "internship", // -> /internships/:slug
    name: "Free 1-Month UPI Internship",
    homeSummary:
      "A free, selection-based internship into UPI and the digital payments ecosystem.",
    tagline:
      "Learn how India's digital payments work — free for selected candidates.",
    duration: "1 Month",
    mode: "Online / Offline / Hybrid",
    fee: "Absolutely Free",
    feeNote: "For selected candidates only",
    isFree: true,
    highlight: false,
    selectionNote:
      "Selection is based on student interest, participation, eligibility, seat availability and screening criteria. This internship is free only for selected candidates.",
    overview:
      "This free 1-Month UPI Internship gives you a practical understanding of the Unified Payments Interface and the broader digital payments ecosystem — from architecture and the NPCI ecosystem to the transaction lifecycle, settlement, production support and real-world case studies.",
    whoShouldJoin: [
      "Students curious about fintech and digital payments",
      "Graduates exploring payments / production-support roles",
      "Anyone wanting real-world UPI domain knowledge",
    ],
    eligibility: [
      "Basic computer knowledge",
      "Genuine interest in digital payments",
      "Selection based on screening criteria (see note below)",
    ],
    outcomes: [
      "Understand UPI architecture and the NPCI ecosystem",
      "Follow the full transaction lifecycle end-to-end",
      "Learn settlement, reconciliation and production support",
      "Apply SQL and Linux basics to payments scenarios",
    ],
    curriculum: [
      "Introduction to UPI",
      "UPI Architecture",
      "NPCI Ecosystem",
      "PSP Applications",
      "Sender and Receiver Banks",
      "Transaction Lifecycle",
      "Settlement and Reconciliation",
      "SQL for Digital Payments",
      "Linux Fundamentals",
      "Production Support",
      "Incident Management",
      "Real-World Case Studies",
    ],
    certification:
      "Selected candidates receive an ASAI Internship Certificate on successful completion.",
    careerSupport:
      "Guidance and career support to help you position UPI/payments skills. Placement assistance only — no guaranteed placement.",
    faqs: [
      { q: "Is this internship really free?", a: "Yes, it is absolutely free — but only for candidates selected through our screening process." },
      { q: "How are candidates selected?", a: "Based on interest, participation, eligibility, seat availability and screening criteria." },
      { q: "Do I need a payments background?", a: "No prior payments background is required — just genuine interest and basic computer knowledge." },
    ],
    related: ["free-ai-ml", "1-year-job-oriented"],
  },

  // -------------------------------------------------------------- FREE INTERNSHIP 2
  {
    slug: "free-ai-ml",
    category: "internship",
    name: "Free 1-Month AI and ML Internship",
    homeSummary:
      "A free, selection-based introduction to AI, Machine Learning and Generative AI.",
    tagline:
      "Start with AI and Machine Learning fundamentals — free for selected candidates.",
    duration: "1 Month",
    mode: "Online / Offline / Hybrid",
    fee: "Absolutely Free",
    feeNote: "For selected candidates only",
    isFree: true,
    highlight: false,
    selectionNote:
      "Selection is based on student interest, participation, eligibility, seat availability and screening criteria. This internship is free only for selected candidates.",
    overview:
      "This free 1-Month AI and ML Internship introduces the fundamentals of Artificial Intelligence and Machine Learning, along with Generative AI, prompt engineering and popular AI tools. You'll get started with Python, build a mini project and explore career opportunities in AI.",
    whoShouldJoin: [
      "Absolute beginners curious about AI and ML",
      "Students wanting a quick, practical AI starter",
      "Anyone exploring a future career in AI",
    ],
    eligibility: [
      "Basic computer knowledge",
      "Interest in AI and Machine Learning",
      "Selection based on screening criteria (see note below)",
    ],
    outcomes: [
      "Grasp AI and Machine Learning fundamentals",
      "Understand Generative AI and prompt engineering",
      "Get started with Python and common AI tools",
      "Build and present a mini project",
    ],
    curriculum: [
      "Artificial Intelligence Fundamentals",
      "Machine Learning Basics",
      "Generative AI",
      "Prompt Engineering",
      "Python Introduction",
      "AI Tools",
      "Career Opportunities in AI",
      "Mini Project",
    ],
    certification:
      "Selected candidates receive an ASAI Internship Certificate on successful completion.",
    careerSupport:
      "Career guidance to help you plan your next steps in AI. Placement assistance only — no guaranteed placement.",
    faqs: [
      { q: "Is this internship free?", a: "Yes, it is absolutely free — only for candidates selected through our screening process." },
      { q: "Is it suitable for beginners?", a: "Absolutely. It is designed as a beginner-friendly introduction to AI and ML." },
      { q: "Will I build a project?", a: "Yes, you'll complete a guided mini project to apply what you learn." },
    ],
    related: ["free-upi", "3-month-internship"],
  },
];

// -------------------------------------------------------------------- Helpers
export const getProgram = (slug) => PROGRAMS_DATA.find((p) => p.slug === slug);

export const getByCategory = (category) =>
  PROGRAMS_DATA.filter((p) => p.category === category);

export const detailPath = (p) =>
  p.category === "internship" ? `/internships/${p.slug}` : `/programs/${p.slug}`;

export const getRelated = (slugs = []) =>
  slugs.map((s) => getProgram(s)).filter(Boolean);

export const waLinkFor = (name) =>
  "https://wa.me/" +
  WHATSAPP.number +
  "?text=" +
  encodeURIComponent(
    `Hello ASAI Team, I am interested in the "${name}". Please share more details.`
  );

// Homepage featured cards (derived — no duplicated content)
const toCard = (slug) => {
  const p = getProgram(slug);
  return {
    name: p.name,
    duration: p.duration,
    description: p.homeSummary,
    mode: p.mode,
    price: p.fee,
    priceNote: p.feeNote,
    detailPath: detailPath(p),
    highlight: p.highlight,
  };
};

export const FEATURED = [
  toCard("3-month-internship"),
  toCard("6-month-industrial-training"),
  toCard("1-year-job-oriented"),
  {
    name: "Free 1-Month Internships",
    duration: "1 Month",
    description: "Free, selection-based internships in UPI and in AI & ML for selected candidates.",
    mode: "Online / Offline / Hybrid",
    price: "Free",
    priceNote: "Selected candidates only",
    detailPath: "/internships",
    highlight: false,
  },
];
